// Dashboard — 我的仓库列表 — Fluent UI v9
//
// 布局规范:
//   1. 顶部工具栏 — Title + 搜索 + Refresh + New
//   2. Card 网格(hoverable + onClick 进详情)
//   3. 空态
//   4. Loading(Spinner)
//   5. 底部分页(0 条不显示)
import { useEffect, useState, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Input,
  Badge,
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  Title3,
  Checkbox,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  ProgressBar,
} from '@fluentui/react-components';
import {
  AddRegular,
  ArrowClockwiseRegular,
  SearchRegular,
  LockClosedRegular,
  GlobeRegular,
  AppFolderRegular,
  DismissRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  DatabaseRegular,
  DeleteRegular,
  CheckmarkRegular,
} from '@fluentui/react-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { reposApi } from '@/api/repos';
import type { PublicRepo } from '@/api/types';
import { explainError } from '@/api/client';
import { HelpPopover } from '@/components/HelpPopover';
import { SimpleList } from '@/components/SimpleList';
import { RepoDetailDialog } from '@/components/RepoDetailDialog';
import { NewRepoDialog } from '@/components/NewRepoDialog';
import { toast } from '@/lib/toast';

const PAGE_SIZE = 9;

const useStyles = makeStyles({
  // 占满 contentBody 可视区高度;内部 body 滚动,分页固定贴底
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXL,
    flexWrap: 'wrap',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    margin: 0,
    fontWeight: 600,
  },
  spacer: {
    flex: 1,
  },
  searchInput: {
    width: '320px',
    maxWidth: '100%',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    paddingBottom: tokens.spacingVerticalL,
  },
  // 列表行内用图标/附加信息(与仓库管理 SimpleList 风格一致)
  repoIcon: {
    fontSize: '22px',
    color: tokens.colorBrandForeground1,
  },
  extraText: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  // 分页固定贴底:body flex:1 吸收全部剩余高度,分页紧跟其后贴在可视区底部
  pagination: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: '10px 0',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  pageInfo: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  // 批量选择模式
  selectModeBar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: '8px 12px',
    marginBottom: tokens.spacingVerticalS,
    backgroundColor: tokens.colorBrandBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorBrandStroke2}`,
  },
  selectModeText: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorBrandForeground1,
    fontWeight: 600,
  },
  deleteProgress: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    marginTop: tokens.spacingVerticalM,
  },
  deleteStats: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    fontSize: tokens.fontSizeBase200,
  },
  successCount: { color: tokens.colorStatusSuccessForeground1 },
  failCount: { color: tokens.colorStatusDangerForeground1 },
  failList: {
    maxHeight: '120px',
    overflowY: 'auto',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorStatusDangerForeground1,
    backgroundColor: tokens.colorStatusDangerBackground1,
    padding: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    fontFamily: tokens.fontFamilyMonospace,
    marginTop: tokens.spacingVerticalS,
  },
  checkboxMedia: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
  },
});

export function DashboardPage() {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [pageIndex, setPageIndex] = useState(1);
  const [queryError, setQueryError] = useState<string | null>(null);
  // 仓库详情对话框
  const [detail, setDetail] = useState<{ owner: string; name: string } | null>(null);
  // 新建仓库对话框
  const [newRepoOpen, setNewRepoOpen] = useState(false);
  // 批量删除模式
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const [deleteResults, setDeleteResults] = useState<
    { name: string; success: boolean; error?: string }[]
  >([]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['my-repos'],
    queryFn: () => reposApi.myRepos(),
  });

  // 搜索框变化 → 跳回第 1 页
  useEffect(() => {
    setPageIndex(1);
  }, [query]);

  // 错误提示(无 ToastProvider,用内联 MessageBar + console.error 替代 message.error)
  useEffect(() => {
    if (error) {
      const msg = explainError(error);
      console.error(msg);
      setQueryError(msg);
    } else {
      setQueryError(null);
    }
  }, [error]);

  const allRepos: PublicRepo[] = data?.repos ?? [];
  const filtered = allRepos.filter(
    (r) =>
      !query ||
      r.name.toLowerCase().includes(query.toLowerCase()) ||
      (r.description?.toLowerCase().includes(query.toLowerCase()) ?? false),
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((pageIndex - 1) * PAGE_SIZE, pageIndex * PAGE_SIZE);

  // ===== 批量选择相关 =====
  const selectedRepos = useMemo(
    () => allRepos.filter((r) => selectedIds.has(String(r.id))),
    [allRepos, selectedIds],
  );

  function toggleSelect(repo: PublicRepo) {
    const id = String(repo.id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((r) => String(r.id))));
    }
  }

  function enterSelectMode() {
    setSelectMode(true);
    setSelectedIds(new Set());
  }

  function exitSelectMode() {
    setSelectMode(false);
    setSelectedIds(new Set());
    setDeleteResults([]);
    setDeleteProgress(0);
  }

  async function handleBatchDelete() {
    if (selectedRepos.length === 0) return;
    setDeleting(true);
    setDeleteProgress(0);
    setDeleteResults([]);
    const results: { name: string; success: boolean; error?: string }[] = [];

    for (let i = 0; i < selectedRepos.length; i++) {
      const repo = selectedRepos[i];
      try {
        await reposApi.delete(repo.owner, repo.name);
        results.push({ name: repo.name, success: true });
      } catch (err) {
        results.push({ name: repo.name, success: false, error: explainError(err) });
      }
      setDeleteProgress(i + 1);
      setDeleteResults([...results]);
    }

    const successCount = results.filter((r) => r.success).length;
    const failCount = results.length - successCount;
    if (successCount > 0) toast.success(`成功删除 ${successCount} 个仓库`);
    if (failCount > 0) toast.error(`${failCount} 个仓库删除失败`);

    if (successCount > 0) {
      queryClient.invalidateQueries({ queryKey: ['my-repos'] });
      setSelectedIds(new Set());
      // 全部成功则退出选择模式
      if (failCount === 0) {
        setSelectMode(false);
        setDeleteConfirmOpen(false);
      }
    }
    setDeleting(false);
  }

  function openRepo(repo: PublicRepo) {
    if (selectMode) {
      toggleSelect(repo);
      return;
    }
    setDetail({ owner: repo.owner, name: repo.name });
  }

  return (
    <div className={styles.root}>
      {/* 1. 顶部工具栏 */}
      <div className={styles.toolbar}>
        <Title3 as="h2" className={styles.title}>
          <AppFolderRegular />
          我的仓库
        </Title3>
        <Input
          className={styles.searchInput}
          contentBefore={<SearchRegular />}
          contentAfter={
            query ? (
              <Button
                appearance="transparent"
                size="small"
                icon={<DismissRegular />}
                aria-label="清除搜索"
                onClick={() => setQuery('')}
              />
            ) : undefined
          }
          placeholder="搜索仓库名或描述"
          value={query}
          onChange={(_, d) => setQuery(d.value)}
        />
        <HelpPopover content="按仓库名或描述模糊搜索你自己的仓库,留空显示全部。" />
        <Button
          appearance="outline"
          icon={isFetching ? <Spinner size="tiny" /> : <ArrowClockwiseRegular />}
          disabled={isFetching}
          onClick={() => refetch()}
        >
          刷新
        </Button>
        {!selectMode && (
          <Button
            appearance="subtle"
            icon={<DeleteRegular />}
            onClick={enterSelectMode}
            disabled={!allRepos.length}
          >
            批量删除
          </Button>
        )}
        <div className={styles.spacer} />
        <Button
          appearance="primary"
          icon={<AddRegular />}
          onClick={() => setNewRepoOpen(true)}
        >
          新建仓库
        </Button>
      </div>

      {/* 查询错误条 */}
      {queryError && (
        <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalL }}>
          <MessageBarBody>{queryError}</MessageBarBody>
        </MessageBar>
      )}

      {/* 批量选择模式状态栏 */}
      {selectMode && (
        <div className={styles.selectModeBar}>
          <Checkbox
            checked={
              selectedIds.size === 0
                ? false
                : selectedIds.size === filtered.length
                  ? true
                  : 'mixed'
            }
            onChange={toggleSelectAll}
            label={
              <span className={styles.selectModeText}>
                已选 {selectedIds.size} / {filtered.length} 个
              </span>
            }
          />
          <div style={{ flex: 1 }} />
          <Button
            appearance="primary"
            icon={<DeleteRegular />}
            disabled={selectedIds.size === 0 || deleting}
            onClick={() => {
              setDeleteResults([]);
              setDeleteProgress(0);
              setDeleteConfirmOpen(true);
            }}
          >
            删除选中
          </Button>
          <Button appearance="secondary" onClick={exitSelectMode}>
            取消
          </Button>
        </div>
      )}

      {/* 2-3. 列表(SimpleList 风格:媒体图标+标题+描述),loading/空态由组件接管 */}
      <div className={styles.body}>
        <SimpleList
          loading={isLoading}
          emptyText={query ? '没有匹配的仓库' : '还没有仓库,新建一个吧'}
          pageSize={Number.POSITIVE_INFINITY}
          items={paged.map((repo) => ({
            key: String(repo.id),
            media: selectMode ? (
              <div className={styles.checkboxMedia}>
                <Checkbox checked={selectedIds.has(String(repo.id))} />
              </div>
            ) : (
              <DatabaseRegular className={styles.repoIcon} />
            ),
            title: (
              <>
                <span>{repo.name}</span>
                {repo.visibility === 'private' ? (
                  <Badge appearance="filled" color="subtle" icon={<LockClosedRegular />}>
                    private
                  </Badge>
                ) : (
                  <Badge appearance="filled" color="brand" icon={<GlobeRegular />}>
                    public
                  </Badge>
                )}
              </>
            ),
            description: repo.description || '(无描述)',
            extra: (
              <span className={styles.extraText}>
                {new Date(repo.updated_at).toLocaleDateString('zh-CN')}
              </span>
            ),
            onClick: () => openRepo(repo),
          }))}
        />
      </div>

      {/* 4. 分页(0 条不显示) */}
      {filtered.length > 0 && (
        <div className={styles.pagination}>
          <Button
            appearance="subtle"
            icon={<ChevronLeftRegular />}
            disabled={pageIndex <= 1}
            onClick={() => setPageIndex((p) => Math.max(1, p - 1))}
          />
          <Text className={styles.pageInfo}>
            第 {pageIndex} / {totalPages} 页 · 共 {filtered.length} 个
          </Text>
          <Button
            appearance="subtle"
            icon={<ChevronRightRegular />}
            disabled={pageIndex >= totalPages}
            onClick={() => setPageIndex((p) => Math.min(totalPages, p + 1))}
          />
        </div>
      )}

      {/* 仓库详情对话框 */}
      <RepoDetailDialog
        owner={detail?.owner ?? null}
        name={detail?.name ?? null}
        onClose={() => setDetail(null)}
      />

      {/* 新建仓库对话框 */}
      <NewRepoDialog
        open={newRepoOpen}
        onClose={() => setNewRepoOpen(false)}
        onCreated={(repo) => {
          setNewRepoOpen(false);
          setDetail({ owner: repo.owner, name: repo.name });
          // 新建成功 → 失效"我的仓库"列表缓存,让新仓库出现在列表里
          queryClient.invalidateQueries({ queryKey: ['my-repos'] });
        }}
      />

      {/* 批量删除确认对话框 */}
      <Dialog
        open={deleteConfirmOpen}
        onOpenChange={(_, d) => {
          if (!d.open && !deleting) setDeleteConfirmOpen(false);
        }}
        modalType="alert"
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>确认删除 {selectedRepos.length} 个仓库？</DialogTitle>
            <DialogContent>
              <p style={{ color: tokens.colorNeutralForeground2, fontSize: 14, margin: 0 }}>
                此操作不可恢复，删除后仓库的代码、提交历史与所有配置将永久丢失。
              </p>
              <div style={{ maxHeight: 160, overflowY: 'auto', marginTop: 12, padding: 8, backgroundColor: tokens.colorNeutralBackground2, borderRadius: 6, fontFamily: tokens.fontFamilyMonospace, fontSize: 13 }}>
                {selectedRepos.map((r) => (
                  <div key={r.id}>
                    <DeleteRegular style={{ marginRight: 6, color: tokens.colorStatusDangerForeground1, fontSize: 14 }} />
                    {r.owner}/{r.name}
                  </div>
                ))}
              </div>
              {(deleting || deleteResults.length > 0) && (
                <div className={styles.deleteProgress}>
                  <ProgressBar
                    value={selectedRepos.length > 0 ? (deleteProgress / selectedRepos.length) * 100 : 0}
                    max={100}
                  />
                  <div className={styles.deleteStats}>
                    <span>进度: {deleteProgress} / {selectedRepos.length}</span>
                    <span className={styles.successCount}>
                      <CheckmarkRegular /> 成功 {deleteResults.filter((r) => r.success).length}
                    </span>
                    <span className={styles.failCount}>
                      <DismissRegular /> 失败 {deleteResults.filter((r) => !r.success).length}
                    </span>
                  </div>
                  {deleteResults.filter((r) => !r.success).length > 0 && (
                    <div className={styles.failList}>
                      {deleteResults
                        .filter((r) => !r.success)
                        .map((r, i) => (
                          <div key={i}>
                            {r.name}: {r.error}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deleting}
              >
                取消
              </Button>
              <Button
                appearance="primary"
                onClick={handleBatchDelete}
                disabled={deleting || selectedRepos.length === 0}
                icon={deleting ? <Spinner size="tiny" appearance="inverted" /> : <DeleteRegular />}
              >
                {deleting ? `删除中 ${deleteProgress}/${selectedRepos.length}` : '确认删除'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
